/**
 * Iubenda CMP / Policy configuration (Epic 33, Story 33-2).
 *
 * Single source of truth for the public Iubenda identifiers the browser needs
 * to render the auto-generated privacy + cookie policies and (later, Story
 * 33-4) the consent banner. These are PUBLIC ids (not secrets) — they are
 * embedded at build time via Vite `import.meta.env.VITE_*`.
 *
 * PROVISIONING (Calin — manual, see docs/EXTERNAL-PROVISIONING-TODO.md):
 * The app ships DORMANT until these env vars are populated with real values
 * from the Iubenda Pro dashboard. Until then `isIubendaConfigured()` returns
 * false and the policy surfaces fall back to the static Story-0.7 page. This
 * mirrors how Epic 7/15 shipped to the SES/Cloudflare boundary and paused on
 * external account setup — no invented credentials.
 *
 * ADR-0005: Iubenda Pro, Consent Mode v2 Advanced, CMP wired via GTM.
 */

/** Iubenda site id (the numeric account/site identifier). */
export const IUBENDA_SITE_ID: string = import.meta.env.VITE_IUBENDA_SITE_ID ?? "";

/** Iubenda privacy-policy document id. */
export const IUBENDA_PRIVACY_POLICY_ID: string =
  import.meta.env.VITE_IUBENDA_PRIVACY_POLICY_ID ?? "";

/**
 * Iubenda cookie-policy document id. Iubenda nests the cookie policy under a
 * privacy-policy document, so this is usually the same id as the privacy
 * policy; kept separate so a divergent setup is expressible without code change.
 */
export const IUBENDA_COOKIE_POLICY_ID: string =
  import.meta.env.VITE_IUBENDA_COOKIE_POLICY_ID ?? IUBENDA_PRIVACY_POLICY_ID;

/** True once the real Iubenda ids are provisioned. Drives every fallback. */
export function isIubendaConfigured(): boolean {
  return IUBENDA_PRIVACY_POLICY_ID.trim().length > 0;
}

export type IubendaPolicyKind = "privacy" | "cookie";

/**
 * Pure URL builder (testable without env). Returns null for a blank id.
 * Iubenda nests the cookie policy under the privacy-policy document path.
 */
export function buildIubendaPolicyUrl(id: string, kind: IubendaPolicyKind): string | null {
  if (!id.trim()) return null;
  const base = `https://www.iubenda.com/privacy-policy/${id.trim()}`;
  return kind === "cookie" ? `${base}/cookie-policy` : base;
}

/**
 * Build the canonical Iubenda-hosted policy URL for the embed anchor.
 * Returns null when unconfigured (callers render the static fallback instead).
 */
export function iubendaPolicyUrl(kind: IubendaPolicyKind): string | null {
  if (!isIubendaConfigured()) return null;
  const id = kind === "cookie" ? IUBENDA_COOKIE_POLICY_ID : IUBENDA_PRIVACY_POLICY_ID;
  return buildIubendaPolicyUrl(id, kind);
}

/** The Iubenda embed loader script, injected once when a policy is rendered. */
export const IUBENDA_LOADER_SRC = "https://cdn.iubenda.com/iubenda.js";
